import { defineComponent, computed, ref, Ref, onMounted, onUnmounted, watch, useTemplateRef, nextTick } from 'vue';
import context,
{
    Constants, ISmsService, ApiResultModel,
    TextConversationModel, SmsMessageType,
    TextConversationStatus, ILookupService, CsrModel,
    ICommunicationsSender, TextMessageModel, TextMessageLogModel,
    RelationshipType, IRelationshipService, RelationshipResult
} from '@truckdown/systems';
import { IAccountData } from '@truckdown/accounts';

interface IProps {
    accountId?: string;
    accountNumber?: number;
    conversationId?: string;
    csrId?: string;
}

interface ISelectedConversation extends TextConversationModel {
    isLast: boolean;
}

interface IData {
    isLoading: Ref<boolean>,

    showNewWindow: Ref<boolean>,
    csrList: Ref<CsrModel[]>,

    selectedCsr: Ref<string>,
    selectedConversation: Ref<ISelectedConversation | undefined>,  //TODO: is last one
    assignToCsr: Ref<string | undefined>,

    conversationList: Ref<TextConversationModel[]>,
    relationships: Ref<RelationshipResult[]>,
    
    errorMessage: Ref<string | undefined>,
    message: Ref<string>,
    fileInput: Ref<HTMLInputElement | null>,
    fileLabel: Ref<string>,
    showMessageOptions: Ref<boolean>,
    messageType: Ref<'Send Message' | 'Send PDF' | 'Send Image'>,
    
    messages: Ref<HTMLInputElement | null>,

    showMessageLoader: Ref<boolean>,
    messagesLoading: Ref<boolean>,
    previous: Ref<TextConversationModel | undefined>,

    conversationInfo: Ref<ISelectedConversation | undefined>,
    infoLoading: Ref<boolean>,
    infoCanAssign: Ref<boolean>,
    infoUserLookups: Ref<RelationshipResult[]>,

    //assign conversation properties
    infoSelectedAccount: Ref<number | undefined>,
    infoSelectedUser: Ref<string>,
    infoAssignError: Ref<string | undefined>,

    //new message
    newMessageDisplay: Ref<boolean>
}

const getData = function (props: IProps) {
    let input = useTemplateRef('fileInput');
    return {
        isLoading: ref<boolean>(true),

        showNewWindow: ref<boolean>(true),
        csrList: ref<CsrModel[]>([]),

        selectedCsr: ref<string>(props.csrId ?? context.user?.id ?? ''),
        selectedConversation: ref<TextConversationModel | undefined>(),
        assignToCsr: ref<string | undefined>(),

        conversationList: ref<TextConversationModel[]>([]),
        relationships: ref<RelationshipResult[]>([]),

        errorMessage: ref<string | undefined>(),
        fileInput: input,
        fileLabel: ref<string>(messageFunctions.getFileLabel(input as any)),
        message: ref<string>(''),
        showMessageOptions: ref<boolean>(false),
        messageType: ref<'Send Message' | 'Send PDF' | 'Send Image'>('Send Message'),

        messages: ref<HTMLDivElement | null>(useTemplateRef('messages') as any),

        showMessageLoader: ref<boolean>(true),
        messagesLoading: ref<boolean>(false),
        previous: ref<TextConversationModel | undefined>(),

        conversationInfo: ref<TextConversationModel | undefined>(),
        infoLoading: ref<boolean>(false),
        infoCanAssign: ref<boolean>(false),
        infoUserLookups: ref<RelationshipResult[]>([]),

        //assign conversation properties
        infoSelectedAccount: ref<number | undefined>(),
        infoSelectedUser: ref<string>(''),
        infoAssignError: ref<string | undefined>(),

        //new message
        newMessageDisplay: ref<boolean>(false)

    } as IData;
}

const rights = {
    canViewAll: true, //context.isInRole(Constants.Roles.SystemRole) || (context.hasClaim('manage-unassigned-messages', 'true')),

    canMessage: function (conversation?: ISelectedConversation) {
        if (!conversation || !conversation.assignedCsr) {
            return false;
        }

        if (conversation.status == TextConversationStatus.Active || conversation.isLast) {
            return true;
        }

        return false;
    },

    canAssign: function (conversation?: ISelectedConversation) {
        if (!conversation || conversation.status != TextConversationStatus.Active || !conversation.isLast) {
            return false;
        }

        return context.isInRole(Constants.Roles.SystemRole)
            || context.user && conversation.assignedCsr == context.user.id
            || (context.hasClaim('manage-unassigned-messages', 'true') && !conversation.assignedCsr);
    },

    canClose: function (conversation?: TextConversationModel) {
        if (!conversation || conversation.status != TextConversationStatus.Active || !context.user) {
            return false;
        }

        return context.isInRole(Constants.Roles.SystemRole) || conversation.assignedCsr == context.user.id;
    },

    canMarkRead: function (conversation?: TextConversationModel) {
        if (!context.user) {
            return false;
        }
        if (conversation && conversation.status == TextConversationStatus.Active && conversation.hasUnread) {
            return context.isInRole(Constants.Roles.SystemRole) || conversation.assignedCsr == context.user.id;
        }
        return false;
    },

    canMarkUnread: function (conversation?: TextConversationModel) {
        if (conversation && conversation.status == TextConversationStatus.Active && !conversation.hasUnread) {
            return true;
        }
        return false;
    }
};

const getComputed = function (props: IProps, data: IData) {
    return {
        canViewAll: computed(() => {
            return rights.canViewAll;
        }),
        canMessage: computed(() => {
            return rights.canMessage(data.selectedConversation.value);
        }),
        canAssign: computed(() => {
            return rights.canAssign(data.selectedConversation.value);
        }),
        canClose: computed(() => {
            return rights.canClose(data.selectedConversation.value);
        }),
        canMarkRead: computed(() => {
            return rights.canMarkRead(data.selectedConversation.value);
        }),
        canMarkUnread: computed(() => {
            return rights.canMarkUnread(data.selectedConversation.value);
        }),
        showFileInput: computed(() => {
            return data.messageType.value == 'Send PDF' || data.messageType.value == 'Send Image';
        }),
        acceptedFileTypes: computed(() => {
            if (data.messageType.value == 'Send PDF') {
                return '.pdf';
            } else if (data.messageType.value == 'Send Image') {
                return '.png, .jpg, .jpeg, .gif, .webp';
            }
            return '';
        }),
        sendMessageIcon: computed(() => {
            if (data.messageType.value == 'Send Message') {
                return 'faMessage';
            } else if (data.messageType.value == 'Send PDF') {
                return 'faFilePdf';
            } else if (data.messageType.value == 'Send Image') {
                return 'faImage';
            }
        })
    };
}

const urlFunctions = {
    dedicatedWindow: null as Window | null,

    newWindow: function () {
        var height = Math.min(screen.height - 50, 900);

        if (this.dedicatedWindow == null || this.dedicatedWindow.closed) {
            this.dedicatedWindow = window.open(location.href + '#iswindow', 'conversation', 'width=550,height=' + height + ',resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no,location=no');
        }
        else {
            this.dedicatedWindow.resizeTo(550, height);
            this.dedicatedWindow.location.href = location.href;
        }

        if (this.dedicatedWindow) {
            this.dedicatedWindow.focus();
        }
    },

    getUrl: function (props: IProps, data: IData) {
        let url = '/systems/conversations';

        if (props.accountId) {
            url = url + '/' + props.accountNumber
        }

        let query: string[] = [];

        if (data.selectedCsr.value != context.user?.id) {
            query.push('csrId=' + encodeURIComponent(data.selectedCsr.value));
        }
        if (data.selectedConversation.value) {
            query.push('id=' + encodeURIComponent(data.selectedConversation.value.id));
        }
        if (query.length > 0) {
            url = url + '?' + query[0];
            if (query.length > 1) {
                for (let i = 1; i < query.length; i++) {
                    url = url + '&' + query[i];
                }
            }
        }

        if (!data.showNewWindow.value) {
            url = url + '#iswindow';
        }
        return url;
    },
    pushState: function (props: IProps, data: IData) {
        window.history.pushState({
            id: data.selectedConversation.value?.id,
            csrId: data.selectedCsr.value
        }, '', this.getUrl(props, data))
    },
    replaceState: function (props: IProps, data: IData) {
        window.history.replaceState({
            id: data.selectedConversation.value?.id,
            csrId: data.selectedCsr.value
        }, '', this.getUrl(props, data))
    },
    popState: async function (props: IProps, data: IData, conversationId?: string, csrId?: string) {
        if (!csrId) {
            csrId = props.csrId ?? context.user?.id ?? '';
        }
        
        data.selectedCsr.value = csrId;
        if (conversationId) {
            await conversationFunctions.loadConversation(data, conversationId);
        } else {
            conversationFunctions.deselectConversation(data);
        }
    },

    scrollToBottom: function (data: IData) {
        nextTick(() => {
            nextTick(() => {
                if (data.messages.value) {
                    data.messages.value.scrollTo({
                        top: data.messages.value.scrollHeight + 50
                    });
                }
            });
        });
    },

    scrollAfterAddingMessages: function (data: IData) {

        if (data.messages.value) {
            const previousHeight = data.messages.value.scrollHeight;
            const previousTop = data.messages.value.scrollTop;
            nextTick(() => {
                nextTick(() => {
                    if (data.messages.value) {
                        const newHeight = data.messages.value.scrollHeight;
                        const heightChange = newHeight - previousHeight;

                        data.messages.value.scrollTo({
                            top: previousTop + heightChange
                        });
                    }
                });
            });
        }
    },

    isImage: function (url: string) {
        if (url.endsWith('.png')
            || url.endsWith('.gif')
            || url.endsWith('.jpg')
            || url.endsWith('.jpeg')
            || url.endsWith('.webp')) {
            return true;
        }
        return false;
    },

    isFileSupported: function (url: string) {
        return this.isImage(url) || url.endsWith('.pdf');
    }
}

const conversationFunctions = {
    loadConversation: async function (data: IData, id: string) {
        var service = await context.getService<ISmsService>('ISmsService');
        var conversation = await service.getConversation(id);

        if (conversation.ok) {
            await this.updateRelationships(data, conversation.data as TextConversationModel);
            this.selectConversation(data, conversation.data as TextConversationModel);
        } else {
            console.log('Error loading conversation', conversation);
        }
    },
    selectConversation: async function (data: IData, conversation: TextConversationModel) {
        let isLast = await this.isLastConversation(conversation);
        let selected = conversation as ISelectedConversation;
        selected.isLast = isLast;
        data.selectedConversation.value = selected;
        data.assignToCsr.value = conversation.assignedCsr;
        data.previous.value = undefined;
        data.showMessageLoader.value = true;

        urlFunctions.scrollToBottom(data);
    },
    deselectConversation: function (data: IData) {
        data.selectedConversation.value = undefined;
        data.assignToCsr.value = undefined;
        data.previous.value = undefined;
    },
    markRead: async function (conversation: TextConversationModel) {
        var service = await context.getService<ISmsService>('ISmsService');
        await service.markRead(conversation.id);
    },
    markUnread: async function (conversation: TextConversationModel) {
        var service = await context.getService<ISmsService>('ISmsService');
        await service.markUnread(conversation.id);
    },
    close: async function (data: IData, conversation: TextConversationModel) {
        let service = await context.getService<ISmsService>('ISmsService');
        let res = await service.closeConversation(conversation.id);
        if (res.ok) {
            this.deselectConversation(data);
        }
    },
    assignCsr: async function (conversation: TextConversationModel, csrId: string) {
        var service = await context.getService<ISmsService>('ISmsService');
        await service.assignConversation(conversation.id, csrId);
    },
    shouldBeInList: function (props: IProps, data: IData, conversation: TextConversationModel) {
        if (props.accountId && props.accountId.length > 0) {
            let account = this.getAccount(data, conversation);
            if (account) {
                return props.accountId == account.id;
            }
        } else {
            if (conversation.status != TextConversationStatus.Active) {
                return false;
            }

            if (conversation.assignedCsr && conversation.assignedCsr == data.selectedCsr.value) {
                return true;
            }

            if (!conversation.assignedCsr && rights.canViewAll && data.selectedCsr.value == context.user?.id) {
                return true;
            }
        }

        return false;
    },
    updateConversation: async function (props: IProps, data: IData, conversation: TextConversationModel) {
        if (data.selectedConversation.value) {
            if (data.selectedConversation.value.externalAddress == conversation.externalAddress) {
                let selected = conversation as ISelectedConversation;
                selected.isLast = await conversationFunctions.isLastConversation(conversation);
                data.selectedConversation.value = selected;
            }

            if (data.selectedConversation.value.externalAddress == conversation.externalAddress) {
                await conversationFunctions.selectConversation(data, conversation);
            }
        }
        
        let idx = data.conversationList.value.findIndex(c => c.externalAddress == conversation.externalAddress);
        if (conversationFunctions.shouldBeInList(props, data, conversation)) {
            await this.updateRelationships(data, conversation);
            if (idx >= 0) {
                data.conversationList.value.splice(idx, 1, conversation);
            } else {
                data.conversationList.value.splice(0, 0, conversation);
            }
        } else {
            if (idx >= 0) {
                data.conversationList.value.splice(idx, 1);
                this.deselectConversation(data);
            }
        }
    },
    updateRelationships: async function (data: IData, conversation: TextConversationModel) {
        var relationships = conversation.relationships;
        var service = await context.getService<IRelationshipService>('IRelationshipService');
        var res = await service.getRelationships({
            relationships: relationships
        });
        data.relationships.value.push(...res);
    },
    saveRelationships: async function (data: IData, conversation: TextConversationModel) {
        var relationships = conversation.relationships;
        var service = await context.getService<ISmsService>('ISmsService');
        var res = await service.updateConversationRelationships(conversation.id, relationships);
        if (res.ok) {
            await this.updateRelationships(data, conversation);
        }
        return res;
    },
    getAccount: function (data: IData, conversation: TextConversationModel) {
        let accountR = conversation.relationships.find(r => r.type == RelationshipType.Account);
        if (accountR) {
            return data.relationships.value.find(r => r.type == RelationshipType.Account && r.id == accountR.id);
        }
        return undefined;
    },
    getUser: function (data: IData, conversation: TextConversationModel) {
        let userR = conversation.relationships.find(r => r.type == RelationshipType.User);
        if (userR) {
            return data.relationships.value.find(r => r.type == RelationshipType.User && r.id == userR.id);
        }
        return undefined;
    },
    isLastConversation: async function (conversation: TextConversationModel) {
        if (conversation.status == TextConversationStatus.Active) {
            return true;
        }

        var service = await context.getService<ISmsService>('ISmsService');
        var search = await service.searchConversations({
            type: SmsMessageType.Support,
            externalAddress: conversation.externalAddress,
            limit: 1
        });

        if (search.ok && search.results) {
            if (search.results.length == 0) {
                return true;
            } else {
                if (search.results[0].id == conversation.id) {
                    return true;
                }
            }
        }

        return false;
    },
    canAssignRelationships: async function (conversation: TextConversationModel) {
        if (rights.canViewAll || conversation.assignedCsr == context.user?.id) {
            if (conversation.status == TextConversationStatus.Active) {
                return true;
            } else {
                return await this.isLastConversation(conversation);
            }
        }
        return false;
    }
};

const messageFunctions = {
    urlRegex: /\b(https:\/\/[^\s<]+)/g,
    consolidateLineBreaks: function (message: string) {
        if (message.length > 0) {
            return message.replace(/(\r?\n){3,}/g, '\n\n');
        }
        return message;
    },
    encodeMessage: function (message: string) {
        const textArea = document.createElement('textarea');
        textArea.innerText = message; // Assign the raw text to innerText, which automatically encodes special characters
        return textArea.innerHTML; // Retrieve the HTML-encoded string from innerHTML
    },
    linkUrls: function (message: string) {
        return message.replace(this.urlRegex, function (url) {
            return '<a class="messageLink" href="' + url + '" target="_blank">' + url + '</a>';
        })
    },
    getFileLabel: function (input: Ref<HTMLInputElement | null>) {
        if (input.value) {
            var file = input.value.files?.item(0);
            if (file) {
                return file.name + '  (Click to change the file or drag a file to upload)';
            }
        }
        return 'Click to select a file or drag a file to upload';
    },
    sendMessage: async function (data: IData, conversation?: ISelectedConversation, file?: File): Promise<ApiResultModel> {
        //TODO: update selected message (isLast) after getting message update
        if (!conversation) {
            return {
                ok: false,
                message: 'No conversation selected.'
            }
        }

        if (rights.canMessage(conversation)) {
            let service = await context.getService<ICommunicationsSender>('ICommunicationsSender');
            let message = {
                from: conversation.internalAddress,
                to: conversation.externalAddress,
                body: data.message.value,
                mediaUrls: [],
                options: {
                    type: SmsMessageType.Support,
                    csr: context.user?.id, //conversation.assignedCsr
                    suppressLog: false,
                    allowAnyAddressInDev: false,
                    relationships: conversation.relationships
                }
            } as TextMessageModel;

            if (file) {
                let smsService = await context.getService<ISmsService>('ISmsService');
                let fileRes = await smsService.uploadFile(file);
                if (fileRes.ok && fileRes.message) {
                    message.mediaUrls = [{
                        value: fileRes.message
                    }];
                } else {
                    return {
                        ok: false,
                        message: 'Error uploading file.'
                    }
                }
            }
            var res = await service.sendSms(message);
            if (res.ok) {
                //if new csr then update conversation?
                conversation.isLast = await conversationFunctions.isLastConversation(conversation);
            }
            return res;
        }

        return {
            ok: false,
            message: 'Cannot send message on selected conversation'
        }
    },

    getMessageText: function (message: TextMessageLogModel) {
        if (message.body.trim().length > 0) {
            return this.linkUrls(this.encodeMessage(this.consolidateLineBreaks(message.body.trim())));
        }
        return undefined;
    },

    loadPreviousMessages: async function (data: IData) {
        if (data.selectedConversation.value) {
            try {
                data.messagesLoading.value = true;

                let lastConversation = data.previous.value ?? data.selectedConversation.value;
                var service = await context.getService<ISmsService>('ISmsService');
                var res = await service.searchConversations({
                    externalAddress: lastConversation.externalAddress,
                    status: TextConversationStatus.Inactive,
                    type: SmsMessageType.Support,
                    lastDate: lastConversation.lastActivityDate,
                    limit: 5
                });

                if (res.ok && res.results) {
                    //add messages
                    if (res.results.length > 0) {
                        let last = res.results[res.results.length - 1];
                        let messages = res.results.flatMap(c => c.messages).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                        last.messages = [
                            ...messages,
                            ...(data.previous.value ? data.previous.value.messages : [])
                        ];
                        data.previous.value = last;

                        urlFunctions.scrollAfterAddingMessages(data);
                    } else {
                        data.showMessageLoader.value = false;
                    }
                } else {
                    console.log(res);
                    data.showMessageLoader.value = false;
                }
            }
            catch (err) {
                console.log(err);
                data.showMessageLoader.value = false;
            }
            finally {
                data.messagesLoading.value = false;
            }
        }
    }
};

const globalFunctions = {
    getTime: function(time: Date | string) {
        let date = new Date(time);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },
    load: async function (props: IProps, data: IData) {
        await this.loadCsrs(data);

        await this.refreshConversations(props, data);

        if (props.conversationId) {
            await conversationFunctions.loadConversation(data, props.conversationId);
        }

        data.isLoading.value = false;
    },

    loadCsrs: async function (data: IData) {
        let service = await context.getService<ILookupService>('ILookupService');
        let csrs = await service.getCsrs();

        let current = csrs.find(c => c.id == context.user?.id);
        let other = csrs.filter(c => c.id != context.user?.id);
        if (current) {
            data.csrList.value = [
                current,
                ...other
            ];
        } else {
            data.csrList.value = [
                ...other
            ];
        }
    },

    refreshConversations: async function (props: IProps, data: IData) {
        var service = await context.getService<ISmsService>('ISmsService');

        if (props.accountId && props.accountId.length > 0) {
            //admin - account screen
            var conversations = await service.getConversations(props.accountId);
            if (conversations.ok) {
                data.conversationList.value = conversations.results ?? [];
                await this.loadRelationships(data);
            } else {
                console.log('Error loading conversations', conversations);
                data.conversationList.value = [];
                data.relationships.value = [];
            }
        } else {
            //global
            var conversations = await (data.selectedCsr.value == context.user?.id
                ? service.getConversations()
                : service.searchConversations({
                    type: SmsMessageType.Support,
                    status: TextConversationStatus.Active,
                    assignedCsr: data.selectedCsr.value,
                    includeUnassigned: false
                }));

            if (conversations.ok) {
                data.conversationList.value = conversations.results ?? [];
                await this.loadRelationships(data);
            } else {
                console.log('Error loading conversations', conversations);
                data.conversationList.value = [];
                data.relationships.value = [];
            }
        }
    },

    loadRelationships: async function (data: IData) {
        var relationships = data.conversationList.value.flatMap(c => c.relationships);
        var service = await context.getService<IRelationshipService>('IRelationshipService');
        var res = await service.getRelationships({
            relationships: relationships
        });
        data.relationships.value = res;
    },

    getUserLookup: async function (accountId: string) {
        var service = await context.getService<IRelationshipService>('IRelationshipService');
        let res = await service.search({
            tags: '#active',
            accountId: accountId,
            type: RelationshipType.User,
            desc: false,
            limit: 999
        });
        if (res.results) {
            return res.results.sort((a, b) => a.display.localeCompare(b.display));
        }
        return [];
    },

    getCsrDisplay: function (data: IData, csrId?: string) {
        if (!csrId || csrId.length == 0) {
            return 'unassigned';
        }

        let csr = data.csrList.value.find(c => c.id == csrId);
        if (csr) {
            return csr.firstName + ' ' + csr.lastName;
        }
        return 'unassigned';
    }
}

const getMethods = function (props: IProps, data: IData) {
    return {
        newWindow: function (ev: Event) {
            ev.preventDefault();
            urlFunctions.newWindow();
        },

        selectConversation: function (conversation: TextConversationModel) {
            conversationFunctions.selectConversation(data, conversation);
        },

        unselectConversation: function () {
            conversationFunctions.deselectConversation(data);
        },

        assignConversation: async function () {
            if (data.conversationInfo.value && !data.infoLoading.value) {
                try {
                    data.infoLoading.value = true;
                    data.infoAssignError.value = undefined;
                    if (await conversationFunctions.canAssignRelationships(data.conversationInfo.value)) {

                        //if has account - assign account and keep screen
                        if (data.infoSelectedAccount.value) {
                            var accountData = await context.getService<IAccountData>('IAccountData');
                            var account = await accountData.getAccountInfo(data.infoSelectedAccount.value);
                            if (account) {
                                data.conversationInfo.value.relationships = [
                                    {
                                        id: account.id,
                                        type: RelationshipType.Account
                                    }
                                ];
                                let res = await conversationFunctions.saveRelationships(data, data.conversationInfo.value);
                                if (res.ok) {
                                    await this.showConversationInfo(data.conversationInfo.value);
                                } else {
                                    console.log(res);
                                    data.infoAssignError.value = 'There was an error assigning the conversation.';
                                }
                            } else {
                                data.infoAssignError.value = 'Invalid account #.';
                            }
                        } else if (data.infoSelectedUser.value && data.infoSelectedUser.value.length > 0) {
                            let user = conversationFunctions.getUser(data, data.conversationInfo.value);
                            if (user?.id == data.infoSelectedUser.value) {
                                this.closeConversationInfo();
                                return;
                            }
                            let currentAccount = conversationFunctions.getAccount(data, data.conversationInfo.value);
                            if (currentAccount) {
                                data.conversationInfo.value.relationships = [
                                    {
                                        id: currentAccount.id,
                                        type: RelationshipType.Account
                                    },
                                    {
                                        id: data.infoSelectedUser.value,
                                        type: RelationshipType.User
                                    }
                                ];

                                let res = await conversationFunctions.saveRelationships(data, data.conversationInfo.value);
                                if (res.ok) {
                                    this.closeConversationInfo();
                                } else {
                                    console.log(res);
                                    data.infoAssignError.value = 'There was an error assigning the conversation.';
                                }
                            } else {
                                data.infoAssignError.value = 'There was an error assigning the conversation.';
                            }
                            
                        } else {
                            data.infoAssignError.value = 'Please select an account or user to assign the conversation to.';
                        }
                    } else {
                        data.infoAssignError.value = 'Cannot assign this conversation.';
                    }

                }
                catch (err) {
                    console.log(err);
                    data.infoAssignError.value = 'There was an error assigning the conversation.';
                }
                finally {
                    data.infoLoading.value = false;
                }
            }
        },

        showConversationInfo: async function (conversation: TextConversationModel) {
            try {
                let isLast = await conversationFunctions.isLastConversation(conversation);
                let selected = conversation as ISelectedConversation;
                selected.isLast = isLast;

                data.infoLoading.value = true;
                data.conversationInfo.value = selected;
                data.infoUserLookups.value = [];
                data.infoSelectedAccount.value = undefined;
                data.infoSelectedUser.value = '';
                data.infoCanAssign.value = false;

                data.infoCanAssign.value = await conversationFunctions.canAssignRelationships(conversation);
                let account = conversationFunctions.getAccount(data, conversation);
                if (account) {
                    data.infoUserLookups.value = await globalFunctions.getUserLookup(account.id);
                    let user = conversationFunctions.getUser(data, conversation);
                    if (user) {
                        data.infoSelectedUser.value = user.id;
                    }
                }
            }
            catch (err) {
                console.log(err);
                data.infoCanAssign.value = false;
                data.infoUserLookups.value = [];
            }
            finally {
                data.infoLoading.value = false;
            }
        },

        closeConversationInfo: function () {
            data.conversationInfo.value = undefined;
            data.infoLoading.value = false;
            data.infoCanAssign.value = false;
            data.infoUserLookups.value = [];
        },

        markRead: async function () {
            if (rights.canMarkRead(data.selectedConversation.value)) {
                await conversationFunctions.markRead(data.selectedConversation.value as TextConversationModel);
            }
        },

        markUnread: async function () {
            if (rights.canMarkUnread(data.selectedConversation.value)) {
                await conversationFunctions.markUnread(data.selectedConversation.value as TextConversationModel);
            }
        },

        close: async function () {
            if (rights.canClose(data.selectedConversation.value)) {
                await conversationFunctions.close(data, data.selectedConversation.value as TextConversationModel);
            }
        },

        assignCsr: async function () {
            if (rights.canAssign(data.selectedConversation.value) && data.assignToCsr.value) {
                await conversationFunctions.assignCsr(
                    data.selectedConversation.value as TextConversationModel, data.assignToCsr.value);
            }
        },

        toggleMessageOptions: function () {
            data.showMessageOptions.value = !data.showMessageOptions.value;
        },
        selectMessage: function () {
            data.messageType.value = 'Send Message';
            data.showMessageOptions.value = false;
        },
        selectPDF: function () {
            data.messageType.value = 'Send PDF';
            data.showMessageOptions.value = false;
        },
        selectImage: function () {
            data.messageType.value = 'Send Image';
            data.showMessageOptions.value = false;
        },
        handleFileChange: function () {
            data.fileLabel.value = messageFunctions.getFileLabel(data.fileInput);
        },
        sendMessage: async function () {
            data.errorMessage.value = undefined;
            data.isLoading.value = true;

            try {
                let file: File | undefined | null = data.messageType.value != 'Send Message' ? data.fileInput.value?.files?.item(0) : undefined;

                if (data.messageType.value != 'Send Message') {
                    if (data.message.value.trim().length == 0 && !file) {
                        data.errorMessage.value = 'Please type a message to send and/or select a file.';
                    }
                } else if (data.message.value.trim().length == 0) {
                    data.errorMessage.value = 'Please type a message to send.';
                }

                if (!data.errorMessage.value) {
                    var res = await messageFunctions.sendMessage(data, data.selectedConversation.value, file ?? undefined);
                    if (res.ok) {
                        //reset message options
                        if (data.fileInput.value) {
                            (data.fileInput.value as any).value = null;
                            data.fileLabel.value = messageFunctions.getFileLabel(data.fileInput);
                        }
                        data.message.value = '';
                        data.showMessageOptions.value = false;
                    } else {
                        console.log(res);
                        data.errorMessage.value = 'There was an error sending the message: ' + res.message;
                    }
                }
            }
            catch (err) {
                console.log(err);
                data.errorMessage.value = 'There was an error sending the message.';
            }
            finally {
                data.isLoading.value = false;
            }
        },
        getMessageText: function (message: TextMessageLogModel) {
            return messageFunctions.getMessageText(message);
        },
        hasAccount: function (conversation: TextConversationModel) {
            return conversationFunctions.getAccount(data, conversation) != undefined;
        },
        getAccount: function (conversation: TextConversationModel) {
            return conversationFunctions.getAccount(data, conversation);
        },
        hasUser: function (conversation: TextConversationModel) {
            return conversationFunctions.getUser(data, conversation) != undefined;
        },
        getUser: function (conversation: TextConversationModel) {
            return conversationFunctions.getUser(data, conversation);
        },
        getTime: globalFunctions.getTime,
        loadPrevious: async function () {
            await messageFunctions.loadPreviousMessages(data);
        },
        isImage: function (url: string) {
            return urlFunctions.isImage(url.toLowerCase());
        },
        isFileSupported: function (url: string) {
            return urlFunctions.isFileSupported(url.toLowerCase());
        },
        getFileName: function (url: string) {
            let idx = url.lastIndexOf('/');
            return url.substring(idx + 1);
        },
        copyToClipboard: async function (data: string) {
            await navigator.clipboard.writeText(data);
        },
        getCsrDisplay: function (csrId?: string) {
            return globalFunctions.getCsrDisplay(data, csrId);
        },
        openNewConversation: function () {
            data.newMessageDisplay.value = true;
        },
        closeNewConversation: function () {
            data.newMessageDisplay.value = false;
        }
    };
}

export default defineComponent({
    props: {
        accountId: {
            type: String,
            required: false
        },
        accountNumber: {
            type: Number,
            required: false
        },
        conversationId: {
            type: String,
            required: false
        },
        csrId: {
            type: String,
            required: false
        }
    },
    setup: function (props: IProps, ctx) {
        var data = getData(props);
        var computed = getComputed(props, data);
        var methods = getMethods(props, data);

        if (location.hash == '#iswindow') {
            data.showNewWindow.value = false;
        }

        let isMounted = false;
        watch(data.selectedConversation, function (newValue: TextConversationModel | undefined, oldValue: TextConversationModel | undefined) {
            if (isMounted) {
                urlFunctions.pushState(props, data);
            } else {
                urlFunctions.replaceState(props, data);
            }

            if (newValue) {
                urlFunctions.scrollToBottom(data);
            }
        });

        watch(data.selectedCsr, async (newValue: string, oldValue: string) => {
            if (isMounted) {
                urlFunctions.pushState(props, data);
            } else {
                urlFunctions.replaceState(props, data);
            }
            conversationFunctions.deselectConversation(data);
            await globalFunctions.refreshConversations(props, data);
        });

        let handleUpdateSupportConversation = async function (conversation?: TextConversationModel) {
            if (conversation) {
                await conversationFunctions.updateConversation(props, data, conversation);
            }
        }

        let HandlePopState = async function (event: PopStateEvent) {
            await urlFunctions.popState(props, data, event.state?.id, event.state?.csrId);
        }

        onMounted(async () => {
            context.events.on('updateSupportConversation', handleUpdateSupportConversation);
            window.addEventListener('popstate', HandlePopState);

            await globalFunctions.load(props, data);
            isMounted = true;
        });

        onUnmounted(() => {
            context.events.off('updateSupportConversation', handleUpdateSupportConversation);
            window.removeEventListener('popstate', HandlePopState);
            isMounted = false;
        });

        return {
            ...data,
            ...computed,
            ...methods,
            SmsMessageType,
            TextConversationStatus
        };
    }
});